import { pick } from 'lodash';
import { distinctUntilChanged, map, type Observable } from 'rxjs';
import { isEqual } from '@ardoq/common-helpers';
import { assistantState$, type AssistantState } from './assistantState$';

/** A readonly tuple of `AssistantState` keys, as produced by `[...] as const`. */
export type AssistantStateKeys = readonly (keyof AssistantState)[];

/**
 * The shape `getScopedAssistantState` emits for a given key tuple. Lets a
 * consumer name the shape from its own key array instead of restating the keys:
 * `type MyState = ScopedAssistantState<typeof MY_STATE_KEYS>;`
 */
export type ScopedAssistantState<T extends AssistantStateKeys> = Pick<
  AssistantState,
  T[number]
>;

/**
 * `assistantState$` is a single reduced stream holding the whole assistant slice,
 * so it emits on every keystroke (`input`) and every scroll frame
 * (`currentScrollTop`). A view model that subscribes to it wholesale therefore
 * re-renders its subtree on typing and scrolling even when it reads neither key.
 *
 * Scoping to the keys a view model actually reads and deduping with `isEqual`
 * stops those emissions at the source. The dedupe lives inside this helper on
 * purpose: every consumer gets the gating for free and cannot forget it.
 */
export const getScopedAssistantState = <const T extends AssistantStateKeys>(
  keys: T
): Observable<ScopedAssistantState<T>> =>
  assistantState$.pipe(
    map(state => pick(state, keys)),
    distinctUntilChanged(isEqual)
  );
